import { Check } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import styles from './Menu.module.css';

export type MenuEntry =
  | {
      label: string;
      icon?: ReactNode;
      onSelect(): void;
      danger?: boolean;
      disabled?: boolean;
      checked?: boolean;
      shortcut?: string;
    }
  | 'separator';

export interface MenuProps {
  trigger(props: {
    onClick(): void;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
  }): ReactNode;
  items: MenuEntry[];
  placement?: 'bottomEnd' | 'bottomStart' | 'topStart' | 'rightStart';
}

/** Dropdown menu with keyboard navigation (arrows, Home/End, Esc). */
export function Menu({ trigger, items, placement = 'bottomEnd' }: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const first = listRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
    first?.focus();
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const buttons = [
      ...(listRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []),
    ];
    const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const focus = (n: number) => buttons[(n + buttons.length) % buttons.length]?.focus();
    if (e.key === 'ArrowDown') focus(i + 1);
    else if (e.key === 'ArrowUp') focus(i - 1);
    else if (e.key === 'Home') focus(0);
    else if (e.key === 'End') focus(buttons.length - 1);
    else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
      if (e.key === 'Escape')
        (rootRef.current?.querySelector('[aria-haspopup]') as HTMLElement | null)?.focus();
    } else return;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className={styles.root} ref={rootRef} onKeyDown={open ? onKeyDown : undefined}>
      {trigger({
        onClick: () => setOpen((o) => !o),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': id,
      })}
      {open && (
        <ul
          id={id}
          ref={listRef}
          role="menu"
          className={[styles.menu, styles[placement]].join(' ')}
        >
          {items.map((item, i) =>
            item === 'separator' ? (
              <li key={`sep${i}`} role="separator" className={styles.separator} />
            ) : (
              <li key={item.label} role="none">
                <button
                  type="button"
                  role={item.checked !== undefined ? 'menuitemradio' : 'menuitem'}
                  aria-checked={item.checked}
                  className={[styles.item, item.danger && styles.danger].filter(Boolean).join(' ')}
                  disabled={item.disabled}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.icon}
                  {item.label}
                  {item.shortcut && <span className={styles.shortcut}>{item.shortcut}</span>}
                  {item.checked && <Check size={16} className={styles.check} />}
                </button>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}
